import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

interface Review {
  id: string;
  buyer_name: string;
  country: string;
  rating: number;
  content: string;
  created_at: string;
}

export default function ReviewTicker() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('is_approved', true)
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`${
              star <= rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'
            } text-sm`}
          ></i>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="h-80 flex items-center justify-center bg-white rounded-lg border border-gray-200">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center bg-white rounded-lg border border-gray-200">
        <p className="text-gray-500">No recent reviews.</p>
      </div>
    );
  }

  const doubledReviews = [...reviews, ...reviews];

  return (
    <div className="relative h-80 bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none"></div>
      
      <div className="h-full overflow-hidden py-4">
        <div className="animate-scroll-up">
          {doubledReviews.map((review, index) => (
            <div
              key={`${review.id}-${index}`}
              className="px-6 py-3 border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full mt-1">
                  <i className="ri-user-line text-blue-600"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <p className="text-sm font-medium text-gray-900">
                      {review.buyer_name}
                    </p>
                    <span className="text-xs text-gray-400">from</span>
                    <p className="text-sm text-gray-600">{review.country}</p>
                  </div>
                  {renderStars(review.rating)}
                  <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                    {review.content}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
